import { useCallback, useEffect, useMemo, useState } from 'react';

// eslint-disable-next-line @typescript-eslint/ban-types
interface IUseLocalStorageOptions<T extends Object> {
  onExternalChange?: (newValue: T | null) => void;
}

// eslint-disable-next-line @typescript-eslint/ban-types
export const useLocalStorage = <T extends Object>(
  key: string,
  initialValue: T,
  options: Partial<IUseLocalStorageOptions<T>> = {}
) => {
  const serialize = useCallback((value: T): string => {
    return JSON.stringify(value);
  }, []);

  const deserialize = useCallback((value: string): T => {
    try {
      return JSON.parse(value);
    } catch (e) {
      return initialValue;
    }
  }, []);

  const [value, setValue] = useState(window.localStorage.getItem(key) ?? serialize(initialValue));

  const updateValue = useCallback(
    (value: T | undefined | ((value: T) => T | undefined)) => {
      if (typeof value === 'function') {
        setValue((oldValue) => {
          const newValue = value(deserialize(oldValue));
          if (typeof newValue === 'undefined') {
            window.localStorage.removeItem(key);
            return serialize(initialValue);
          } else {
            const serialized = serialize(newValue);
            window.localStorage.setItem(key, serialized);
            return serialized;
          }
        });
      } else if (typeof value === 'undefined') {
        window.localStorage.removeItem(key);
        setValue(serialize(initialValue));
      } else {
        const serialized = serialize(value);
        window.localStorage.setItem(key, serialized);
        setValue(serialized);
      }
    },
    [serialize]
  );

  // const updateValueFn = useCallback(
  //   (valueUpdater: (value: T | undefined) => T | undefined) => {
  //
  //   },
  //   [serialize]
  // );

  useEffect(() => {
    setValue(window.localStorage.getItem(key) ?? serialize(initialValue));
  }, [key]);

  /**
   * this effect reacts to changes that happened in another browser tab
   */
  useEffect(() => {
    const listener = (event: StorageEvent) => {
      if (event.key === key) {
        options.onExternalChange?.(event.newValue ? deserialize(event.newValue) : null);

        if (event.newValue) {
          setValue(event.newValue);
        } else {
          setValue(serialize(initialValue));
        }
      }
    };

    window.addEventListener('storage', listener);
    return () => {
      window.removeEventListener('storage', listener);
    };
  }, [key, options.onExternalChange, deserialize]);

  const deserializedValue = useMemo(() => deserialize(value), [value, deserialize]);

  return [deserializedValue, updateValue] as const; // as [T, (value: T) => void];
};
